
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import type { AxiosError } from 'axios';

import axios from 'axios';

import {
  defineComponent,
  ref,
  reactive,
  computed,
  onMounted,
} from 'vue';

import { useStore } from 'vuex';

import { useRouter } from 'vue-router';

import { useI18n } from 'vue-i18n';

import { useToast } from 'primevue/usetoast';

import PrimeVueInputText from 'primevue/inputtext';

import PrimeVuePassword from 'primevue/password';

import OTPInput from 'vue3-otp-input';

import { VueRecaptcha } from 'vue-recaptcha';

import type {
  ErrorsMap,
  Project,
} from '@/types';

import {
  THEME_LIGHT,
  PROJECT_ID_ADBISON,
  USER_ROLE_PUBLISHER,
  USER_ROLE_ADVERTISER,
  USER_ROLES,
} from '@/libs/consts';

export default defineComponent({
  components: {
    PrimeVueInputText,
    PrimeVuePassword,
    OTPInput,
    VueRecaptcha,
  },
  setup() {
    const store = useStore();

    const router = useRouter();

    const i18n = useI18n();

    const toast = useToast();

    store.commit('SET_THEME', THEME_LIGHT);

    const project = computed((): Project => store.getters.project);

    const userRoles = [...USER_ROLES].map((role) => ({
      ...role,
      disabled: project.value.id === PROJECT_ID_ADBISON && role.value === USER_ROLE_PUBLISHER,
    }));

    const form = reactive({
      role: project.value.id === PROJECT_ID_ADBISON ? USER_ROLE_ADVERTISER : (project.value.preferred_user_role || USER_ROLE_PUBLISHER),
      email: '',
      email_verification_code: '',
      password: '',
      repeat_password: '',
      firstname: '',
      lastname: '',
      telegram: '',
      teams: '',
      recaptcha_token: '',
      link_slug: window.localStorage.getItem('link_slug'),
      manager_ids: project.value.id === PROJECT_ID_ADBISON ? [2859] : [], // #2859 oleg@adbison.com
    });

    const hasInvite = ref(false);

    const registrationClosed = computed(() => project.value.id === PROJECT_ID_ADBISON && !hasInvite.value);

    const accept = ref<boolean>(false);

    const acceptAdvertiser = ref<boolean>(false);

    const errorsMap = ref<ErrorsMap>({});

    const sendEmailVerificationCodeLoading = ref<boolean>(false);

    const sendEmailVerificationCode = async (): Promise<void> => {
      sendEmailVerificationCodeLoading.value = true;

      try {
        await axios.post('/api/v3/email-verification-code', {
          email: form.email,
        });

        toast.add({
          severity: 'success',
          summary: i18n.t('success'),
          detail: 'Email verification code sent',
          life: 2000,
        });
      } catch (error: unknown | Error | AxiosError) {
        // В случае ошибки валидации API возвращает ответ с кодом 422
        if (axios.isAxiosError(error) && error.response?.status === 422) {
          errorsMap.value = error.response.data;
        } else {
          toast.add({
            severity: 'error',
            summary: i18n.t('error'),
            detail: i18n.t('unknown_error'),
            life: 2000,
          });
        }
      }

      sendEmailVerificationCodeLoading.value = false;
    };

    const recaptchaVerify = (token: string): void => {
      form.recaptcha_token = token;
    };

    const registerLoading = ref<boolean>(false);

    const register = async (): Promise<void> => {
      if (registrationClosed.value) {
        return;
      }

      errorsMap.value = {};

      registerLoading.value = true;

      try {
        const response = await axios.post('/api/user/register', form);

        store.commit('SET_TOKEN', response.data.token);

        await router.push('/statistics/publisher/direct');
      } catch (error: unknown | Error | AxiosError) {
        // В случае ошибки валидации API возвращает ответ с кодом 422
        if (axios.isAxiosError(error) && error.response?.status === 422) {
          errorsMap.value = error.response.data;
        } else {
          toast.add({
            severity: 'error',
            summary: i18n.t('error'),
            detail: i18n.t('unknown_error'),
            life: 2000,
          });
        }
      }

      registerLoading.value = false;
    };

    onMounted(() => {
      const params = new URLSearchParams(window.location.search);

      const inviteParam = params.get('i');

      if (!inviteParam) {
        return;
      }

      try {
        const invite = JSON.parse(atob(inviteParam));

        if (!invite || typeof invite !== 'object' || Array.isArray(invite)) {
          return;
        }

        Object.assign(form, invite);

        hasInvite.value = true;
      } catch {
        // Invalid invites must leave self-registration closed
      }
    });

    return {
      PROJECT_ID_ADBISON,
      USER_ROLE_ADVERTISER,
      project,
      userRoles,
      form,
      hasInvite,
      registrationClosed,
      accept,
      acceptAdvertiser,
      errorsMap,
      sendEmailVerificationCodeLoading,
      sendEmailVerificationCode,
      recaptchaVerify,
      registerLoading,
      register,
    };
  },
});
